/**
 * The roof as the editor shows it while it is being edited: the saved roof model with the unsaved vertex moves and
 * face pitch/direction changes applied the way the server applies them (app/design/editor/roof.py): a turned face
 * keeps its eave, a vertex sits at the mean height of the planes of its faces, uv frames, areas and edge lengths
 * follow. The server recomputes all of it on save; this is for live display.
 */
import type { S } from "../api/client";
import {
  dist3,
  normalFrom,
  pitchAzimuth,
  planToUv,
  uvAxes,
  zOnPlane,
  type UvFrame,
  type Vec2,
  type Vec3,
} from "./geo.ts";
import { polygonArea, type Area } from "./snap.ts";

export interface RoofEdits {
  vertices: Record<string, Vec2>;
  faces: Record<string, { pitch_deg?: number; azimuth_deg?: number }>;
}

export const NO_ROOF_EDITS: RoofEdits = { vertices: {}, faces: {} };

export interface LiveFace {
  id: string;
  label: string;
  boundary: string[];
  holes: string[][];
  normal: Vec3;
  d: number;
  pitch: number;
  azimuth: number;
  frame: UvFrame;
  /** Outline in plan (x, y). */
  plan: Vec2[];
  holesPlan: Vec2[][];
  area: number;
  groundArea: number;
  /** Changed by unsaved edits (its saved setbacks no longer apply). */
  edited: boolean;
  /** The building it belongs to (null: the main one). */
  structureId?: string | null;
}

export interface LiveEdge {
  id: string;
  v: [string, string];
  type: string;
  faces: string[];
  length: number;
}

export interface LiveRoof {
  vertices: Record<string, Vec3>;
  faces: LiveFace[];
  edges: LiveEdge[];
  area: number;
  groundArea: number;
  predominantPitch: number | null;
}

const EAVE_TOLERANCE_M = 0.05;

export function liveRoof(doc: S["RoofModelOutput"], edits: RoofEdits = NO_ROOF_EDITS): LiveRoof {
  const base: Record<string, Vec3> = Object.fromEntries(doc.vertices.map((v) => [v.id, v.xyz as Vec3]));
  const vertices: Record<string, Vec3> = { ...base };
  const moved = new Set<string>();
  for (const [id, xy] of Object.entries(edits.vertices)) {
    const b = base[id];
    if (!b) continue;
    if (Math.hypot(xy[0] - b[0], xy[1] - b[1]) > 1e-4) {
      vertices[id] = [xy[0], xy[1], b[2]];
      moved.add(id);
    }
  }
  const planes: Record<string, { normal: Vec3; d: number }> = {};
  const turned = new Set<string>();
  for (const f of doc.faces) {
    planes[f.id] = { normal: f.plane.normal as Vec3, d: f.plane.d };
    const e = edits.faces[f.id];
    if (!e) continue;
    const pitch = e.pitch_deg ?? f.plane.pitch_deg;
    const azimuth = (((e.azimuth_deg ?? f.plane.azimuth_deg) % 360) + 360) % 360;
    const dAz = Math.abs(((azimuth - f.plane.azimuth_deg + 540) % 360) - 180);
    if (Math.abs(pitch - f.plane.pitch_deg) < 0.01 && dAz < 0.01) continue;
    const ring = f.boundary.map((id) => base[id]!);
    const low = Math.min(...ring.map((p) => p[2]));
    const eave = ring.filter((p) => p[2] <= low + EAVE_TOLERANCE_M);
    const anchor: Vec3 = [0, 1, 2].map((i) => eave.reduce((s, p) => s + p[i]!, 0) / eave.length) as Vec3;
    const normal = normalFrom(pitch, azimuth);
    planes[f.id] = { normal, d: -(normal[0] * anchor[0] + normal[1] * anchor[1] + normal[2] * anchor[2]) };
    turned.add(f.id);
  }
  const touching: Record<string, string[]> = {};
  for (const f of doc.faces)
    for (const ring of [f.boundary, ...(f.holes ?? [])]) for (const v of ring) (touching[v] ??= []).push(f.id);
  const changedFaces = new Set(turned);
  for (const [v, faces] of Object.entries(touching)) {
    if (!moved.has(v) && !faces.some((f) => turned.has(f))) continue;
    const [x, y] = vertices[v]!;
    const z = faces.reduce((s, f) => s + zOnPlane(planes[f]!.normal, planes[f]!.d, x, y), 0) / faces.length;
    vertices[v] = [x, y, z];
    faces.forEach((f) => changedFaces.add(f));
  }
  const faces: LiveFace[] = doc.faces.map((f) => {
    const { normal, d } = planes[f.id]!;
    const edited = changedFaces.has(f.id);
    const plan = f.boundary.map((v) => [vertices[v]![0], vertices[v]![1]] as Vec2);
    const holesPlan = (f.holes ?? []).map((h) => h.map((v) => [vertices[v]![0], vertices[v]![1]] as Vec2));
    const ground = Math.abs(polygonArea(plan)) - holesPlan.reduce((s, h) => s + Math.abs(polygonArea(h)), 0);
    const { pitch, azimuth } = pitchAzimuth(normal);
    let frame = f.uv_frame as UvFrame;
    if (edited) {
      // as the server: new axes, and the origin keeps its place in plan (lifted onto the new plane), so modules
      // placed in uv stay put when a corner moves
      const { u, v } = uvAxes(normal);
      const [ox, oy] = f.uv_frame.origin as Vec3;
      frame = { origin: [ox, oy, zOnPlane(normal, d, ox, oy)], u, v };
    }
    return {
      id: f.id,
      label: f.label,
      boundary: f.boundary,
      holes: f.holes ?? [],
      normal,
      d,
      pitch,
      azimuth,
      frame,
      plan,
      holesPlan,
      groundArea: ground,
      area: ground / Math.max(normal[2], 1e-3),
      edited,
      structureId: f.structure_id ?? null,
    };
  });
  const edges: LiveEdge[] = doc.edges.map((e) => ({
    id: e.id,
    v: [e.v[0]!, e.v[1]!],
    type: e.type,
    faces: e.faces,
    length: dist3(vertices[e.v[0]!]!, vertices[e.v[1]!]!),
  }));
  const area = faces.reduce((s, f) => s + f.area, 0);
  const biggest = faces.reduce<LiveFace | null>((b, f) => (!b || f.area > b.area ? f : b), null);
  return {
    vertices,
    faces,
    edges,
    area,
    groundArea: faces.reduce((s, f) => s + f.groundArea, 0),
    predominantPitch: biggest ? biggest.pitch : null,
  };
}

/** The face whose plan outline contains a point (the highest one where faces overlap in plan). */
export function faceAt(roof: LiveRoof, p: Vec2): LiveFace | null {
  let best: LiveFace | null = null;
  let bestZ = -Infinity;
  for (const f of roof.faces) {
    if (!inRing(p, f.plan) || f.holesPlan.some((h) => inRing(p, h))) continue;
    const z = zOnPlane(f.normal, f.d, p[0], p[1]);
    if (z > bestZ) {
      best = f;
      bestZ = z;
    }
  }
  return best;
}

function inRing([x, y]: Vec2, ring: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The face outline in its own uv frame. */
export function faceArea(face: LiveFace): Area {
  const toUv = (p: Vec2) => planToUv(face.frame, face.normal, face.d, p);
  return { exterior: face.plan.map(toUv), holes: face.holesPlan.map((h) => h.map(toUv)) };
}

/** Where modules may go on a face: the saved setbacks, or the whole face when the face was edited (setbacks are
 * recomputed on save) or has none. */
export function usableAreas(face: LiveFace, setbacks: S["SetbacksOutput"] | null | undefined): Area[] {
  if (face.edited || !setbacks) return [faceArea(face)];
  const own = setbacks.faces.find((f) => f.face_id === face.id);
  if (!own) return [faceArea(face)];
  return own.usable.map((u) => ({
    exterior: u.exterior as Vec2[],
    holes: (u.holes ?? []) as Vec2[][],
  }));
}
